import React, { useMemo, useCallback } from 'react';
import type { SortBy } from 'tea-component/lib/table/addons';
import TableCommonHook from '@op/components/TableCommonHook';
import useLanguage from '@op/hooks/useLanguage';
import { useVersionAnalysisList } from '@op/pages/SteamPerformance/hooks/useVersionAnalysisList';
import { DBO_V, DBO_TM } from '@op/utils/format';
import { calculateSteamReviewRate } from '@op/utils/text';
import { goStatsDetailAction } from '@op/pages/stats/Detail';
import RatioBar from './RatioBar';
import Sentiment from '@op/components/Snippets/Sentiment';
import TextBubbles from '@home/components/TextBubbles';
import type { IVersionConfig } from '@op/pages/SteamPerformance/VersionAnalysis/config';

/** 维度列配置项 */
interface IDimensionColumn {
  key: string;
  label: string;
  width: number;
  showFor: string[];
}

/** 根据版本配置动态生成维度列：每个层级在自身及更深层级时显示 */
const buildDimensionColumns = (
  versionConfig: IVersionConfig,
): IDimensionColumn[] =>
  versionConfig.levels.map((level, index) => ({
    key: level.value,
    label: level.label,
    width: 140,
    showFor: versionConfig.levels.slice(index).map((l) => l.value),
  }));

/** 指标列配置 */
const METRIC_COLUMNS = [
  { key: 'rating', label: 'Rating', width: 220 },
  { key: 'number_of_reviews', label: 'Number of Reviews', width: 220 },
  { key: 'positive_reviews', label: 'Positive Reviews', width: 220 },
  { key: 'positive_ratio', label: 'Positive Ratio', width: 220 },
  { key: 'negative_reviews', label: 'Negative Reviews', width: 220 },
  { key: 'negative_ratio', label: 'Negative Ratio', width: 220 },
] as const;

/** 格式化比率（0.0~1.0 -> 百分比文本） */
const formatRatio = (value: number): string => {
  if (value == null || isNaN(value)) return '-';
  return DBO_V.ratio(value * 100);
};

interface IVersionTableProps {
  filter: Record<string, any>;
  /** 配置游戏模式下传入的分组维度，不传则按非配置游戏渲染（显示 Update Title 列） */
  groupBy?: string;
  /** 当前 tab 是否激活（默认 true） */
  isSelected?: boolean;
  visibleColumns: string[];
  /** 版本配置（配置游戏模式下用于动态生成维度列） */
  versionConfig?: IVersionConfig | null;
  /** 排序状态（由父组件受控传入） */
  sortInfo: SortBy<any>;
  /** 排序变化回调，供父组件收集当前排序状态用于下载 */
  onSortInfoChange?: (sortInfo: SortBy<any>) => void;
}

const VersionTable: React.FC<IVersionTableProps> = ({
  filter,
  groupBy,
  isSelected = true,
  visibleColumns,
  versionConfig,
  sortInfo,
  onSortInfoChange,
}) => {
  const { formatMessage, isEn } = useLanguage();

  const {
    tableData,
    loading,
    total,
    maxReviews,
    pageInfo,
    setPageInfo,
  } = useVersionAnalysisList({
    filter,
    groupBy,
    isSelected,
    sortInfo,
  });

  // 跳转到详情页
  const handleNavigateToDetails = useCallback(
    (record: any, type?: string) => {
      const navFilter: Record<string, any> = {
        ...filter,
        start_time: record.start_time,
        end_time: record.end_time,
        date_type: 'daily',
      };

      // 配置游戏（version_filters 模式）：用 record 的维度值构建 version_filters
      if (versionConfig?.version_filters) {
        const levelKeys = versionConfig.levels.map((l) => l.value);
        const versionFilterObj: Record<string, string> = {};
        levelKeys.forEach((key) => {
          if (record[key]) {
            versionFilterObj[key] = String(record[key]);
          }
        });
        navFilter.version_filters = [versionFilterObj];
        delete navFilter.chapters;
        delete navFilter.releases;
      }

      goStatsDetailAction({
        path: '/opinion/SteamPerformance/VersionAnalysis/Details',
        params: {
          filter: navFilter,
          extraConfig: {},
          record,
          type,
        },
      });
    },
    [filter, versionConfig],
  );

  // 动态列配置
  const columns = useMemo(() => {
    const cols: any[] = [];

    if (groupBy && versionConfig) {
      // 配置游戏：按维度展示列（从 versionConfig 动态生成）
      const dimensionColumns = buildDimensionColumns(versionConfig);
      dimensionColumns.forEach((col) => {
        if (!col.showFor.includes(groupBy)) return;
        const colWithRender = col as {
          render?: (record: any) => React.ReactNode;
        };
        const colRender = colWithRender.render;
        cols.push({
          key: col.key,
          header: formatMessage({ id: col.label }),
          width: col.width,
          render: (record: any) => {
            if (groupBy === col.key) {
              return (
                <a
                  className="dbo-version-analysis-table__link"
                  onClick={() => handleNavigateToDetails(record, col.key)}
                >
                  {record[col.key]}
                </a>
              );
            }
            return colRender ? colRender(record) : record[col.key];
          },
        });
      });
    } else {
      // 非 Dune 游戏：显示 Update Title（可点击跳转）
      cols.push({
        key: 'update_title_en',
        header: formatMessage({ id: 'Update Title' }),
        width: 200,
        render: (record: any) => (
          <a
            className="dbo-version-analysis-table__link"
            onClick={() => handleNavigateToDetails(record)}
          >
            <TextBubbles text={isEn ? record.update_title_en : record.update_title_cn || record.update_title_en} />
          </a>
        ),
      });
    }

    // 固定列：Time Interval
    cols.push({
      key: 'time_interval',
      header: formatMessage({ id: 'Time Interval' }),
      width: 230,
      render: (record: any) =>
        DBO_TM.timeTitle({
          start_time: record.start_time,
          end_time: record.end_time,
          date_type: 'daily',
        }),
    });

    // 固定列：Release Date
    cols.push({
      key: 'release_date',
      header: formatMessage({ id: 'Release Date' }),
      width: 200,
    });

    // 可选列：Rating（受 visibleColumns 控制）
    if (visibleColumns.includes('rating')) {
      cols.push({
        key: 'rating',
        header: formatMessage({ id: 'Rating-1' }),
        width: 180,
        render: (record: any) => {
          const value = record.positive_ratio;

          if (value === null || value === undefined || isNaN(value) || record?.number_of_reviews === 0) {
            return '-';
          }
          const ratingText = calculateSteamReviewRate(value);
          let sentiment: number;
          if (ratingText === 'Mixed') {
            sentiment = 3;
          } else if (
            [
              'Mostly Positive',
              'Very Positive',
              'Overwhelmingly Positive',
            ].includes(ratingText)
          ) {
            sentiment = 5;
          } else {
            sentiment = 1;
          }
          return (
            <div className="dbo-table-td-sentiment">
              <Sentiment sentiment={sentiment} showValue={false} />
              <span>{formatMessage(ratingText)}</span>
            </div>
          );
        },
      });
    }

    // 其他指标列（受 visibleColumns 控制）
    METRIC_COLUMNS.forEach((col) => {
      if (col.key === 'rating') return;
      if (!visibleColumns.includes(col.key)) return;
      const renderMap: Record<string, (record: any) => React.ReactNode> = {
        number_of_reviews: (record) => (
          <RatioBar value={record.number_of_reviews} max={maxReviews} />
        ),
        positive_reviews: (record) => (
          <RatioBar
            value={record.positive_reviews}
            max={record.number_of_reviews}
            type="positive"
          />
        ),
        positive_ratio: (record) => (
          <RatioBar
            value={record.positive_ratio}
            max={1}
            type="positive"
            format={formatRatio}
          />
        ),
        negative_reviews: (record) => (
          <RatioBar
            value={record.negative_reviews}
            max={record.number_of_reviews}
            type="negative"
          />
        ),
        negative_ratio: (record) => (
          <RatioBar
            value={record.negative_ratio}
            max={1}
            type="negative"
            format={formatRatio}
          />
        ),
      };
      cols.push({
        key: col.key,
        header: formatMessage({ id: col.label }),
        width: col.width,
        align: 'right',
        render: renderMap[col.key],
      });
    });

    return cols;
  }, [
    groupBy,
    versionConfig,
    visibleColumns,
    maxReviews,
    isEn,
    formatMessage,
    handleNavigateToDetails,
  ]);

  // 可排序列
  const sortColumns = useMemo(() => {
    const base = [
      'release_date',
      'number_of_reviews',
      'positive_reviews',
      'positive_ratio',
      'negative_reviews',
      'negative_ratio',
    ];
    return groupBy && versionConfig
      ? [...base, ...versionConfig.levels.map((l) => l.value)]
      : base;
  }, [groupBy, versionConfig]);

  return (
    <div className="dbo-version-analysis-table">
      <TableCommonHook
        columns={columns}
        records={tableData}
        total={total}
        bordered="all"
        loading={loading}
        pageInfo={pageInfo}
        setPageInfo={setPageInfo}
        sortInfo={[sortInfo, (sort: SortBy<any>) => onSortInfoChange?.({ by: sort.by, order: sort.order || 'desc' })]}
        sortColumns={sortColumns}
        sortType="server"
        defaultSort={{ by: 'release_date', order: 'desc' }}
        logBtnId="version-analysis-table"
      />
    </div>
  );
};

export default React.memo(VersionTable);
